import {
  getFilteredPolicies,
  IPolicyDoc,
  IUserAppSettings,
  PolicyAction,
  PolicyResource,
  StatusBarBehavior,
} from "@vmosolutions/oc-interfaces";
import { IUser } from "src/redux/state/User";
import { ActionType, IAppAction } from "./../actions/Helpers";

/**
 * a function to return a functin that can take in a field name and tell you if the user has permissions
 * @param policies- a user's policies
 * @param resource- the resouce that you are requesting the polcies for
 */
const resourcePermissionFunction = (policies: IPolicyDoc[]): any => {
  /**
   * A function to return a boolean based on permissions for a resource and/of each field in resource.
   * @param resource - PolicyResource Enum
   * @param fieldName - string
   */
  const hasPermissions = (resource: PolicyResource, fieldName?: string, action?: PolicyAction): boolean => {
    if (!resource || policies?.length === 0) {
      return false;
    }

    const [allowedPolicies, deniedPolicies] = getFilteredPolicies(policies, resource);

    // DENIED ACCESS
    for (const pol of deniedPolicies) {
      if (pol.action.includes(action ?? PolicyAction.Update) || pol.action.includes("*")) {
        if (fieldName && pol.fields?.length > 0) {
          for (const field of pol.fields) {
            if (field === "*" || new RegExp(field).test(fieldName)) {
              // If fieldName is passed in as denied, return false
              return false;
            }
          }
        } else if (!fieldName && pol.fields?.length && pol.fields?.includes("*")) {
          // If fieldName isn't passed in but all fields are denied, return false
          return false;
        }
      }
    }

    // ALLOWED ACCESS
    for (const pol of allowedPolicies) {
      if (pol.action.includes(PolicyAction.Update) || pol.action.includes("*")) {
        if (fieldName && pol.fields?.length > 0) {
          for (const field of pol.fields) {
            if (field === "*" || new RegExp(field).test(fieldName)) {
              // If fieldName is passed in as allowed, return true
              return true;
            }
          }
        } else if (!fieldName && pol.fields?.length && pol.fields.includes("*")) {
          // If fieldName isn't passed in but all fields are allowed, return true
          return true;
        }
      }
    }

    //!! If none of the above policies return a boolean, disallow access!!
    return false;
  };
  return hasPermissions;
};

const initialState: IUser = {
  attributes: {
    email: null,

    email_verified: false,
    firstName: null,
    lastName: null,
    name: null,
    permissions: {},
    policies: [],
    sub: null,
    _id: null,
    allowInternalSignIn: false,
  },
  hasPermissions: resourcePermissionFunction([]),
  id: null,
  username: null,
  locale: "en",
  appSettings: {
    mosaicLayout: null,
    timelineAircraftFilters: null,
    crewScheduleFilters: null,
    flightTimelineRowHeightPercentage: 1.0,
    flightTimelineViewScale: "week",
    flightTimeline: {
      followCurrentDate: false,
      currentDateOffsetHours: 0,
    },
    blockInfo: {
      utc: true,
      icao: true,
    },
    savedSearchFilters: [],
    statusBarBehavior: StatusBarBehavior.Sticky,
  },
};

export const AuthenticationReducer = (state: IUser = initialState, action: IAppAction): IUser => {
  switch (action.type) {
    case ActionType.LOGIN_REQUEST: {
      // console.log(action.payload.attributes?.appSettings);
      const policies = action.payload.attributes.policies ?? [];
      const authentication = { ...action.payload, hasPermissions: resourcePermissionFunction(policies) };
      const loadedAppSettings = action.payload?.attributes?.appSettings ?? {};
      return {
        ...state,
        ...authentication,
        appSettings: { ...state.appSettings, ...loadedAppSettings },
      };
    }
    case ActionType.LOGOUT_REQUEST:
      return null;
    case ActionType.UPDATE_USER_APP_SETTINGS: {
      const newAppSettings = action.payload as IUserAppSettings;
      /** Pass through a switch and only update the property the action wishes to update, if undefined, updates all */
      if (!action.updateOnly) {
        return {
          ...state,
          appSettings: { ...state.appSettings, ...newAppSettings },
        };
      }
      switch (action.updateOnly) {
        case "mosaicLayout":
          return {
            ...state,
            appSettings: { ...state.appSettings, mosaicLayout: newAppSettings.mosaicLayout },
          };
        case "timelineAircraftFilters":
          return {
            ...state,
            appSettings: { ...state.appSettings, timelineAircraftFilters: newAppSettings.timelineAircraftFilters },
          };
        case "crewScheduleFilters":
          return {
            ...state,
            appSettings: { ...state.appSettings, crewScheduleFilters: newAppSettings.crewScheduleFilters },
          };
        case "flightTimelineRowHeightPercentage":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              flightTimelineRowHeightPercentage: newAppSettings.flightTimelineRowHeightPercentage,
            },
          };
        case "flightTimelineViewScale":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              flightTimelineViewScale: newAppSettings.flightTimelineViewScale,
            },
          };
        case "flightDisplayMenuCentered":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              flightDisplayMenuCentered: newAppSettings.flightDisplayMenuCentered,
            },
          };
        case "blockInfo":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              blockInfo: { ...state.appSettings.blockInfo, ...newAppSettings.blockInfo },
            },
          };
        case "highlightFlightSettings":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              highlightFlightSettings: newAppSettings.highlightFlightSettings,
            },
          };
        case "followCurrentDate":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              flightTimeline: { ...state.appSettings.flightTimeline, ...newAppSettings.flightTimeline },
            },
          };
        case "savedSearchFilters":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              savedSearchFilters: newAppSettings.savedSearchFilters,
            },
          };
        case "statusBarBehavior":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              statusBarBehavior: newAppSettings.statusBarBehavior,
            },
          };
        case "crewScheduleHideTooltips":
          return {
            ...state,
            appSettings: {
              ...state.appSettings,
              crewScheduleHideTooltips: newAppSettings.crewScheduleHideTooltips,
            },
          };
        //!! Changing the entire appSettings object is too dangerous so if a specific child object isn't specified by the call, change nothing! --CC
        default:
          return state;
        //  return { ...state, appSettings: { ...state.appSettings, ...newAppSettings } };
      }
    }
    default:
      return state;
  }
};
